/**
 * Shared React hooks built on top of CoreAPI.
 *
 * `useFirestoreSubscription` is the only abstraction modules should
 * use to subscribe to live Firestore data. Direct `onSnapshot` calls
 * are forbidden per monitoring/CLAUDE.md §2 (and procedure §4) —
 * they leak listeners when components unmount during navigation or
 * when the tab goes into the background. This hook handles both:
 *
 *  - Detaches the listener on component unmount.
 *  - Detaches when the tab is hidden (Page Visibility API), re-attaches
 *    on visibilitychange → "visible". Cuts Firestore read costs while
 *    the user is on a different tab and avoids the listener heartbeat
 *    racing with mobile background-throttling.
 *
 * The hook returns the latest snapshot data (or `null` until the
 * first read), plus the loading / error state.
 */
import { useEffect, useState } from "react";
import {
  type DocumentData,
  type FirestoreDataConverter,
  type Query,
  collection,
  doc,
  onSnapshot,
  query,
  where,
  type DocumentReference,
} from "firebase/firestore";
import { useCore } from "./react";

export interface SubscriptionResult<T> {
  readonly data: T | null;
  readonly loading: boolean;
  readonly error: Error | null;
}

interface DocPathSpec {
  readonly kind: "doc";
  readonly path: string;
}

interface CollectionPathSpec {
  readonly kind: "collection";
  readonly path: string;
  readonly where?: ReadonlyArray<readonly [string, "==" | "<" | ">=", unknown]>;
}

export type SubscriptionSpec = DocPathSpec | CollectionPathSpec;

/**
 * Subscribe to a Firestore doc or collection. Returns null until the
 * first snapshot lands; updates on each subsequent change. The
 * listener auto-detaches when the tab is hidden and re-attaches on
 * `visibilitychange → visible`. Strict equality on the path so the
 * effect cleanup runs on path change (no stale subscriptions).
 */
export function useFirestoreSubscription<T = DocumentData>(
  spec: SubscriptionSpec | null,
  converter?: FirestoreDataConverter<T>,
): SubscriptionResult<T extends Array<infer _> ? T : T[] | T> {
  const core = useCore();
  const [data, setData] = useState<unknown>(null);
  const [loading, setLoading] = useState<boolean>(spec !== null);
  const [error, setError] = useState<Error | null>(null);

  // Cache a stable key for the effect deps (objects-by-reference would
  // re-subscribe every render).
  const cacheKey = spec ? JSON.stringify(spec) : null;

  useEffect(() => {
    if (!spec || !cacheKey) {
      setData(null);
      setLoading(false);
      return;
    }

    let unsub: (() => void) | null = null;
    let detached = false;

    const fb = (core as unknown as { _firebase?: { db?: unknown } })._firebase;
    const db = fb?.db as Parameters<typeof collection>[0] | undefined;
    if (!db) {
      setError(new Error("useFirestoreSubscription: firestore not configured"));
      setLoading(false);
      return;
    }

    const attach = () => {
      if (unsub) return;
      try {
        if (spec.kind === "doc") {
          let ref: DocumentReference<DocumentData> = doc(db, spec.path);
          if (converter) {
            // @ts-expect-error narrowing
            ref = ref.withConverter(converter);
          }
          unsub = onSnapshot(
            ref,
            (snap) => {
              if (detached) return;
              setData(snap.exists() ? snap.data() : null);
              setLoading(false);
            },
            (e) => {
              if (detached) return;
              setError(e instanceof Error ? e : new Error(String(e)));
              setLoading(false);
            },
          );
        } else {
          let q: Query<DocumentData> = collection(db, spec.path);
          for (const [field, op, value] of spec.where ?? []) {
            q = query(q, where(field, op, value));
          }
          if (converter) {
            // @ts-expect-error narrowing
            q = q.withConverter(converter);
          }
          unsub = onSnapshot(
            q,
            (snap) => {
              if (detached) return;
              setData(snap.docs.map((d) => d.data()));
              setLoading(false);
            },
            (e) => {
              if (detached) return;
              setError(e instanceof Error ? e : new Error(String(e)));
              setLoading(false);
            },
          );
        }
      } catch (e) {
        setError(e instanceof Error ? e : new Error(String(e)));
        setLoading(false);
      }
    };

    const detach = () => {
      if (unsub) {
        unsub();
        unsub = null;
      }
    };

    // Initial subscribe + visibility-aware re-subscribe.
    const onVisChange = () => {
      if (document.visibilityState === "visible") attach();
      else detach();
    };
    if (typeof document === "undefined" || document.visibilityState === "visible") {
      attach();
    }
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisChange);
    }

    return () => {
      detached = true;
      detach();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisChange);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  return { data: data as never, loading, error };
}
